import type { ResultPayload } from "@mesketch/engine";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router";
import { ItemScreen } from "../components/ItemScreen.js";
import { ErrorBox, PreviewBar, useNoIndex, useTitle } from "../components/Layout.js";
import { Drawing, RoundResult, Sketch } from "../components/lazy.js";
import { PauseDialog } from "../components/PauseDialog.js";
import { TextsProvider } from "../components/texts.js";
import { Tone } from "../components/tone.js";
import { ApiError, api, type RatingFormat, type ResultView, type RoundItems, type SessionState, type Texts } from "../data/api.js";
import { device, type SessionRecord } from "../data/device.js";
import { offlineRoundResult } from "../data/offline.js";
import { ResponseSync } from "../data/sync.js";

// Průchod testem (REQ-019 až REQ-023, REQ-031): položky kola, uložení odpovědí, výsledek kola
// ze serveru a obrazovka mezi koly, po posledním kole finální výsledek a Skica. Online klient
// výsledek nepočítá, jen kreslí payload serveru (DEC-006, REQ-022 AK 2).

/** Zpoždění posunu po klepnutí (REQ-020 AK 2). */
export const ADVANCE_MS = 250;

type Phase =
  | { kind: "loading" }
  | { kind: "lost" }
  /** Odkaz z pauzy se vyzvedl na jiném zařízení (CR-013): data testu se tady smažou. */
  | { kind: "moved" }
  /** Srovnávací skupina před první otázkou (DEC-007, normy po pohlavích). */
  | { kind: "respondent"; saving: boolean }
  | { kind: "error"; offline: boolean; retry: () => void }
  | { kind: "items"; data: RoundItems; index: number; selected: number | null }
  | { kind: "completing" }
  | { kind: "between"; round: number; payload: ResultPayload | null; view: ResultView | null; texts: Texts; offline?: boolean; portraitSeed?: number | null }
  | { kind: "sketch"; payload: ResultPayload; view: ResultView; texts: Texts };

interface Ctx {
  readonly rec: SessionRecord;
  readonly state: SessionState;
  readonly sync: ResponseSync;
}

export function SessionPage() {
  const intl = useIntl();
  const lang = intl.locale;
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [pausing, setPausing] = useState(false);
  // Jazyk rozhraní se může změnit uprostřed testu (REQ-019 AK 6): sezení se kvůli tomu znovu
  // nenačítá, navigace jen použije aktuální jazyk.
  const langRef = useRef(lang);
  langRef.current = lang;
  const ctx = useRef<Ctx | null>(null);
  const shownAt = useRef(0);
  /** Položky kola 1 načtené souběžně se stavem čerstvého sezení (NFR-003: o krok sítě méně). */
  const prefetched = useRef<{ round: number; data: Promise<RoundItems | null> } | null>(null);
  const advancing = useRef<ReturnType<typeof setTimeout> | null>(null);
  useNoIndex();
  useTitle(undefined);

  const fail = useCallback(
    (e: unknown, retry: () => void) => {
      if (e instanceof ApiError && (e.status === 404 || e.status === 410)) {
        // Záznam v zařízení je, ale token už neplatí: test pokračuje jinde (CR-013).
        void device.session(id).then((rec) => {
          if (rec === undefined) setPhase({ kind: "lost" });
          else void device.removeSession(id).then(() => setPhase({ kind: "moved" }));
        });
      } else setPhase({ kind: "error", offline: e instanceof ApiError && e.offline, retry });
    },
    [id],
  );

  const showItems = useCallback(
    async (round: number) => {
      const c = ctx.current;
      if (c === null) return;
      const early = prefetched.current?.round === round ? await prefetched.current.data : null;
      prefetched.current = null;
      const data = early ?? (await api.items(id, c.rec.token, round));
      const first = data.items.findIndex((it) => c.sync.answer(it.id) === undefined);
      const index = first < 0 ? data.items.length - 1 : first;
      shownAt.current = performance.now();
      setPhase({ kind: "items", data, index, selected: valueOf(c, data, index) });
    },
    [id],
  );

  const finish = useCallback(async () => {
    const c = ctx.current;
    if (c === null) return;
    setPhase({ kind: "completing" });
    const res = await api.complete(id, c.rec.token);
    const { texts, view } = await api.texts(id, c.rec.token);
    const token = res.result_token ?? c.rec.resultToken ?? null;
    // Datum dokončení pro návrat k výsledku na úvodu testu (REQ-041).
    const rec: SessionRecord = { ...c.rec, done: true, resultToken: token, answered: c.rec.total, finishedAt: new Date().toISOString() };
    await device.saveSession(rec);
    // Čerstvě dokončený test: výsledková stránka se nejdřív zeptá, kam Skicu uložit (REQ-033).
    if (token !== null) navigate(`/${langRef.current}/r/${token}`, { replace: true, state: { fresh: true } });
    else if (res.payload !== undefined) setPhase({ kind: "sketch", payload: res.payload, view, texts });
    else navigate(`/${langRef.current}/`, { replace: true });
  }, [id, navigate]);

  const completeRound = useCallback(
    async (round: number) => {
      const c = ctx.current;
      if (c === null) return;
      setPhase({ kind: "completing" });
      try {
        await c.sync.flush();
        const res = await api.completeRound(id, c.rec.token, round);
        if (round >= c.state.rounds) {
          await finish();
          return;
        }
        const { texts, view } = await api.texts(id, c.rec.token);
        setPhase({ kind: "between", round, payload: res.payload ?? null, view: res.payload === undefined ? null : view, texts, portraitSeed: res.portrait_seed ?? null });
      } catch (e) {
        // Bez připojení na konci kola označený náhled tímtéž enginem (REQ-022 AK 3). Poslední kolo
        // potřebuje server (finální výsledek a odkaz).
        const bundle = e instanceof ApiError && e.offline && round < c.state.rounds ? await device.offline(id) : undefined;
        if (bundle !== undefined) {
          try {
            const payload = await offlineRoundResult(bundle, id, round, c.sync.all());
            setPhase({ kind: "between", round, payload, view: bundle.view, texts: bundle.texts, offline: true });
            window.addEventListener("online", () => void completeRound(round), { once: true });
            return;
          } catch {
            // Náhled se nepovedl, zůstane chyba bez připojení.
          }
        }
        fail(e, () => void completeRound(round));
      }
    },
    [id, fail, finish],
  );

  const load = useCallback(async () => {
    setPhase({ kind: "loading" });
    try {
      const rec = await device.session(id);
      if (rec === undefined) {
        setPhase({ kind: "lost" });
        return;
      }
      if (rec.answered === 0) prefetched.current = { round: 1, data: api.items(id, rec.token, 1).catch(() => null) };
      const state = await api.state(id, rec.token);
      ctx.current?.sync.close();
      const sync = await ResponseSync.open(id, rec.token, state);
      ctx.current = { rec, state, sync };
      if (state.final) {
        if (rec.resultToken) navigate(`/${langRef.current}/r/${rec.resultToken}`, { replace: true });
        else await finish();
        return;
      }
      if (state.respondentFields?.includes("sex") === true && (state.respondentSex ?? null) === null && state.answered.length === 0) {
        setPhase({ kind: "respondent", saving: false });
        return;
      }
      const round = Array.from({ length: state.rounds }, (_, i) => i + 1).find((r) => !state.completedRounds.includes(r));
      if (round === undefined) {
        await finish();
        return;
      }
      const prev = round - 1;
      const plan = state.plan[round - 1] ?? [];
      if (prev >= 1 && sync.answeredIn(plan) === 0) {
        // Návrat po „Dokončit později“: tatáž obrazovka mezi koly (REQ-022 AK 7).
        const res = await api.completeRound(id, rec.token, prev);
        const { texts, view } = await api.texts(id, rec.token);
        setPhase({ kind: "between", round: prev, payload: res.payload ?? null, view: res.payload === undefined ? null : view, texts, portraitSeed: res.portrait_seed ?? null });
        return;
      }
      if (plan.length > 0 && sync.answeredIn(plan) === plan.length) {
        await completeRound(round);
        return;
      }
      await showItems(round);
    } catch (e) {
      fail(e, () => void load());
    }
  }, [id, navigate, finish, completeRound, showItems, fail]);

  useEffect(() => {
    // Data pro náhled bez připojení se obnoví při každém načtení sezení (NFR-003), až po první
    // obrazovce, aby nebrala přenos položkám kola.
    void load().then(() => {
      const rec = ctx.current?.rec;
      if (rec !== undefined)
        void api.offline(id, rec.token).then(
          (b) => device.saveOffline(id, b),
          () => undefined,
        );
    });
    return () => {
      ctx.current?.sync.close();
      if (advancing.current !== null) clearTimeout(advancing.current);
    };
  }, [load]);

  const c = ctx.current;
  const preview = c?.state.origin === "preview";
  const home = useCallback(() => void navigate(`/${langRef.current}/`), [navigate]);
  const flush = useCallback(async () => {
    try {
      await ctx.current?.sync.flush();
      return navigator.onLine !== false;
    } catch {
      return false;
    }
  }, []);
  // Zpět z pauzy na tutéž obrazovku; čas odpovědi na položku se měří znovu od návratu (DES-002).
  const resumeTest = useCallback(() => {
    setPausing(false);
    shownAt.current = performance.now();
  }, []);
  // „Dát si pauzu“ (CR-013): s e-mailem jen u sezení user a se zapnutým e-mailem, jinak domů.
  const later = () => {
    if (c?.state.pause_email === true) {
      setPausing(true);
      return;
    }
    void c?.sync.flush().catch(() => undefined);
    home();
  };

  async function answer(value: number) {
    if (phase.kind !== "items" || c === null || advancing.current !== null) return;
    const item = phase.data.items[phase.index];
    if (item === undefined) return;
    const duration = Math.max(0, Math.round(performance.now() - shownAt.current));
    setPhase({ ...phase, selected: value });
    await c.sync.record(item.id, phase.data.round, [value], duration);
    const all = c.state.plan.flat();
    void device.saveSession({ ...c.rec, answered: c.sync.answeredIn(all), total: all.length });
    advancing.current = setTimeout(() => {
      advancing.current = null;
      const next = phase.index + 1;
      if (next < phase.data.items.length) {
        shownAt.current = performance.now();
        setPhase({ kind: "items", data: phase.data, index: next, selected: valueOf(c, phase.data, next) });
      } else void completeRound(phase.data.round);
    }, ADVANCE_MS);
  }

  function back() {
    if (phase.kind !== "items" || c === null || phase.index === 0) return;
    const index = phase.index - 1;
    shownAt.current = performance.now();
    setPhase({ ...phase, index, selected: valueOf(c, phase.data, index) });
  }

  let body;
  switch (phase.kind) {
    case "loading":
      body = null;
      break;
    case "lost":
      body = (
        <p className="notice notice--quiet">
          <FormattedMessage id="test.sessionLost" />
        </p>
      );
      break;
    case "respondent":
      body = (
        <section className="stack" data-testid="respondent">
          <h1>
            <FormattedMessage id="respondent.title" />
          </h1>
          <p>
            <FormattedMessage id="respondent.lead" />
          </p>
          <div className="stack">
            {(["f", "m"] as const).map((sex) => (
              <button
                key={sex}
                type="button"
                className="button button--quiet"
                disabled={phase.saving}
                data-sex={sex}
                onClick={() => {
                  const cur = ctx.current;
                  if (cur === null) return;
                  setPhase({ kind: "respondent", saving: true });
                  void api.setRespondent(id, cur.rec.token, sex).then(
                    () => void load(),
                    (e: unknown) => fail(e, () => void load()),
                  );
                }}
              >
                <FormattedMessage id={`respondent.${sex}`} />
              </button>
            ))}
          </div>
          <p className="small muted">
            <FormattedMessage id="respondent.note" />
          </p>
        </section>
      );
      break;
    case "moved":
      body = (
        <p className="notice notice--quiet" data-testid="moved">
          <FormattedMessage id="test.otherDevice" />
        </p>
      );
      break;
    case "error":
      body = <ErrorBox offline={phase.offline} onRetry={phase.retry} />;
      break;
    case "completing":
      body = <Drawing />;
      break;
    case "items": {
      const item = phase.data.items[phase.index];
      const format = item === undefined ? undefined : phase.data.formats[item.format];
      body =
        item === undefined || format === undefined || format.kind !== "rating" ? (
          <ErrorBox />
        ) : (
          <TextsProvider texts={phase.data.texts} locale={c?.state.locale ?? lang} preview={preview}>
            <ItemScreen
              itemId={item.id}
              format={format as RatingFormat}
              round={phase.data.round}
              rounds={c?.state.rounds ?? 1}
              index={phase.index}
              count={phase.data.items.length}
              selected={phase.selected}
              instructionsKey={phase.data.instructions}
              onAnswer={(v) => void answer(v)}
              onBack={phase.index === 0 ? null : back}
              onLater={later}
            />
          </TextsProvider>
        );
      break;
    }
    case "between":
      body = (
        <TextsProvider texts={phase.texts} locale={c?.state.locale ?? lang} preview={preview}>
          <RoundResult
            round={phase.round}
            rounds={c?.state.rounds ?? phase.round + 1}
            payload={phase.payload}
            view={phase.view}
            nextItems={c?.state.plan[phase.round]?.length ?? 0}
            offline={phase.offline === true}
            portraitSeed={phase.portraitSeed ?? null}
            onContinue={() => void showItems(phase.round + 1).catch((e: unknown) => fail(e, () => void load()))}
            onLater={later}
          />
        </TextsProvider>
      );
      break;
    case "sketch":
      body = (
        <TextsProvider texts={phase.texts} locale={c?.state.locale ?? lang} preview={preview}>
          <p className="notice notice--quiet small">
            <FormattedMessage id="sketch.noLink" />
          </p>
          <Sketch payload={phase.payload} view={phase.view} owner shareUrl={null} feedback={() => null} onRevoke={null} revoked={false} />
        </TextsProvider>
      );
      break;
  }
  return (
    <>
      {preview ? <PreviewBar /> : null}
      <Tone slug={c?.rec.slug ?? ""}>
        <Suspense fallback={<Drawing />}>
          {pausing && c !== null ? <PauseDialog flush={flush} send={(email) => api.resumeEmail(id, c.rec.token, email)} onHome={home} onBack={resumeTest} /> : body}
        </Suspense>
      </Tone>
    </>
  );
}

function valueOf(c: Ctx, data: RoundItems, index: number): number | null {
  const id = data.items[index]?.id;
  return id === undefined ? null : (c.sync.answer(id)?.value[0] ?? null);
}
